// UX67: shared helpers for the "what's moving" temporal organization
// layer. The feed sort + timeframe filter + "What's moving" strip all
// read activity from the same source so a bill's position across all
// three surfaces is consistent.

// Returns the most recent legislative-activity date for an item, as a
// millisecond epoch, with graceful fallbacks:
//   1. Latest billActions[].action_date (server-overlaid)
//   2. item.lastActionDate (static tag, extracted in parseEvent)
//   3. item.postedAt (Date object; when the bot posted the poll)
// Pulse polls (botName === 'pulse') return 0 so they're excluded from
// activity-based sorts without changing their visibility.
export function effectiveActivityMs(item) {
  if (!item) return 0
  if (item.botName === 'pulse') return 0
  // Candidate races have no legislative action concept — fall through
  // to postedAt.
  if (item.kind !== 'candidate' && Array.isArray(item.billActions) && item.billActions.length) {
    const latest = item.billActions[item.billActions.length - 1]
    const t = parseYmdOrIso(latest?.action_date)
    if (t > 0) return t
  }
  if (item.lastActionDate) {
    const t = parseYmdOrIso(item.lastActionDate)
    if (t > 0) return t
  }
  if (item.postedAt instanceof Date) return item.postedAt.getTime()
  if (typeof item.postedAt === 'number') return item.postedAt * 1000
  return 0
}

// Return the ISO date (YYYY-MM-DD) of the activity, or '' when unknown.
export function effectiveActivityIso(item) {
  const ms = effectiveActivityMs(item)
  if (!ms) return ''
  const d = new Date(ms)
  const y = d.getUTCFullYear()
  const m = String(d.getUTCMonth() + 1).padStart(2, '0')
  const day = String(d.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

// Short display label of the latest action, e.g. "Referred to Committee"
// or "Passed Senate." Empty string when no actions are known.
export function latestActionLabel(item) {
  if (!item) return ''
  if (Array.isArray(item.billActions) && item.billActions.length) {
    const latest = item.billActions[item.billActions.length - 1]
    const txt = (latest?.action_text || '').trim()
    if (txt) return txt
  }
  return (item.lastAction || '').trim()
}

// Timeframe window helpers. Returns the oldest millisecond epoch that
// still counts as "in the window." A null return means "no cutoff."
export const TIMEFRAMES = ['all', 'today', 'week', 'month']

export function timeframeCutoffMs(timeframe) {
  if (!timeframe || timeframe === 'all') return null
  const now = Date.now()
  const DAY = 24 * 60 * 60 * 1000
  if (timeframe === 'today') return now - 1 * DAY
  if (timeframe === 'week')  return now - 7 * DAY
  if (timeframe === 'month') return now - 30 * DAY
  return null
}

export function timeframeLabel(timeframe) {
  if (timeframe === 'today') return 'Today'
  if (timeframe === 'week')  return 'This week'
  if (timeframe === 'month') return 'This month'
  return 'Any time'
}

// --- internals ---

function parseYmdOrIso(s) {
  if (!s || typeof s !== 'string') return 0
  // Short "YYYY-MM-DD" form from Congress.gov actions.
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const t = Date.parse(s + 'T00:00:00Z')
    return Number.isFinite(t) ? t : 0
  }
  const t = Date.parse(s)
  return Number.isFinite(t) ? t : 0
}
